import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Collapse, Box, Typography, IconButton, Divider, Tooltip, Chip, Avatar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ListAlt as ListAltIcon,
  NoteAdd as NoteAddIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Link as LinkIcon,
  PersonAdd as PersonAddIcon,
  EmojiEvents as EmojiEventsIcon,
  Redeem as RedeemIcon,
  BusinessCenter as BusinessCenterIcon,
  Send as SendIcon,
  AccountTree as AccountTreeIcon,
  AttachMoney as AttachMoneyIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ManageAccounts as ManageAccountsIcon,
  Approval as ApprovalIcon,
  Settings as SettingsIcon,
  CardGiftcard as CardGiftcardIcon,
  Inventory as InventoryIcon,
  Campaign as CampaignIcon,
  Share as ShareIcon,
  GroupAdd as GroupAddIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  ExpandLess, ExpandMore,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getSidebarSections, getRoleLabel, getRoleColor } from '../../utils/permissions';

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;
const PRIMARY_COLOR = '#1B365D';
const SECONDARY_COLOR = '#00B894';

// All possible nav sections, keyed by role-specific IDs
const allNavSections = {
  // Free User main
  main: {
    id: 'main', label: 'Main',
    items: [{ label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }],
  },
  // Admin main
  main_admin: {
    id: 'main_admin', label: 'Main',
    items: [{ label: 'Admin Dashboard', icon: <AdminPanelSettingsIcon />, path: '/admin' }],
  },
  // Partner main
  main_partner: {
    id: 'main_partner', label: 'Main',
    items: [{ label: 'Partner Dashboard', icon: <BusinessCenterIcon />, path: '/partner' }],
  },
  // Free User applications
  applications: {
    id: 'applications', label: 'Applications',
    items: [
      { label: 'Product Listing', icon: <ListAltIcon />, path: '/products' },
      { label: 'Apply Now', icon: <NoteAddIcon />, path: '/apply' },
      { label: 'My Applications', icon: <DescriptionIcon />, path: '/my-applications' },
    ],
  },
  // Free User referral
  referral: {
    id: 'referral', label: 'Referral',
    items: [
      { label: 'Invite Friends', icon: <ShareIcon />, path: '/invite' },
      { label: 'My Referrals', icon: <PeopleIcon />, path: '/referral-history' },
      { label: 'Referral Links', icon: <LinkIcon />, path: '/referral-links' },
      { label: 'Marketing Materials', icon: <CampaignIcon />, path: '/partner/marketing' },
    ],
  },
  // Free User rewards
  rewards: {
    id: 'rewards', label: 'Rewards',
    items: [
      { label: 'My Rewards', icon: <EmojiEventsIcon />, path: '/rewards' },
      { label: 'Redemption Center', icon: <RedeemIcon />, path: '/redemption' },
      { label: 'My Profile', icon: <PersonIcon />, path: '/profile' },
    ],
  },
  // Partner portal
  partner: {
    id: 'partner', label: 'Partner Portal',
    items: [
      { label: 'Submit Application', icon: <SendIcon />, path: '/partner/submit' },
      { label: 'My Applications', icon: <DescriptionIcon />, path: '/partner/applications' },
      { label: 'My Downline', icon: <AccountTreeIcon />, path: '/partner/downline' },
      { label: 'Commission', icon: <AttachMoneyIcon />, path: '/partner/commission' },
      { label: 'Marketing Materials', icon: <CampaignIcon />, path: '/partner/marketing' },
      { label: 'Product Listing', icon: <ListAltIcon />, path: '/products' },
      { label: 'My Profile', icon: <PersonIcon />, path: '/profile' },
    ],
  },
  // Admin section
  admin: {
    id: 'admin', label: 'Admin',
    items: [
      { label: 'User Management', icon: <GroupAddIcon />, path: '/admin/users' },
      { label: 'Sales Listing', icon: <AssessmentIcon />, path: '/admin/sales-listing' },
      { label: 'Referral Management', icon: <ManageAccountsIcon />, path: '/admin/referrals' },
      { label: 'Partner Approval', icon: <ApprovalIcon />, path: '/admin/partners' },
      { label: 'Product & Commission', icon: <InventoryIcon />, path: '/admin/product-config' },
      { label: 'Reward Config', icon: <CardGiftcardIcon />, path: '/admin/reward-config' },
      { label: 'Marketing Materials', icon: <CampaignIcon />, path: '/admin/marketing' },
      { label: 'Campaign Settings', icon: <SettingsIcon />, path: '/admin/campaign-settings' },
      { label: 'Product Listing', icon: <ListAltIcon />, path: '/products' },
      { label: 'My Profile', icon: <PersonIcon />, path: '/profile' },
    ],
  },
  // Individual Agent view
  agent_view: {
    id: 'agent_view', label: 'Agent Portal',
    items: [
      { label: 'Dashboard', icon: <DashboardIcon />, path: '/agent' },
      { label: 'Submit Application', icon: <SendIcon />, path: '/agent/submit' },
      { label: 'My Applications', icon: <DescriptionIcon />, path: '/agent/sales' },
      { label: 'Commission Table', icon: <TableChartIcon />, path: '/commission-table' },
      { label: 'Marketing Materials', icon: <CampaignIcon />, path: '/partner/marketing' },
      { label: 'Product Listing', icon: <ListAltIcon />, path: '/products' },
      { label: 'My Profile', icon: <PersonIcon />, path: '/profile' },
    ],
  },
};

const Sidebar = ({ open, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get role-filtered sections
  const visibleSections = useMemo(() => {
    const sectionKeys = getSidebarSections(user?.role);
    return sectionKeys.map((key) => allNavSections[key]).filter(Boolean);
  }, [user?.role]);

  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = {};
    Object.keys(allNavSections).forEach((key) => { initial[key] = true; });
    return initial;
  });

  const handleSectionToggle = (sectionId) => {
    if (!open) return;
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const isActive = (path) => location.pathname === path;
  const drawerWidth = open ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth, flexShrink: 0, whiteSpace: 'nowrap',
        transition: (theme) => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: drawerWidth, overflowX: 'hidden',
          backgroundColor: PRIMARY_COLOR, color: '#ffffff', borderRight: 'none',
          transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      {/* Logo Area */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        justifyContent: open ? 'space-between' : 'center',
        px: open ? 2.5 : 1, py: 2, minHeight: 64,
      }}>
        {open ? (
          <Typography variant="h5" sx={{
            fontWeight: 800, letterSpacing: 1,
            background: `linear-gradient(135deg, #ffffff 0%, ${SECONDARY_COLOR} 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', userSelect: 'none',
          }}>
            iMoney
          </Typography>
        ) : (
          <Typography variant="h6" sx={{ fontWeight: 800, color: SECONDARY_COLOR, userSelect: 'none' }}>
            iM
          </Typography>
        )}
        {open && (
          <IconButton onClick={onToggle} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* Role Badge */}
      {open && user && (
        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Box sx={{
            backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 2, p: 1.5,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Avatar sx={{
              width: 36, height: 36, fontSize: '0.75rem', fontWeight: 700,
              backgroundColor: getRoleColor(user.role),
            }}>
              {user.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.name}
              </Typography>
              <Chip
                label={getRoleLabel(user.role)}
                size="small"
                sx={{
                  height: 18, fontSize: '0.6rem', fontWeight: 700,
                  backgroundColor: getRoleColor(user.role), color: 'white',
                }}
              />
            </Box>
          </Box>
          {user.role === 'free_user' && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip label={user.referralLevel || 'Silver'} size="small" sx={{
                height: 18, fontSize: '0.6rem', fontWeight: 700,
                backgroundColor: user.referralLevel === 'Platinum' ? '#7B1FA2' : user.referralLevel === 'Gold' ? '#F57F17' : '#546E7A',
                color: 'white',
              }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {user.tokenBalance || 0} tokens
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        {visibleSections.map((section) => (
          <React.Fragment key={section.id}>
            {open ? (
              <ListItemButton
                onClick={() => handleSectionToggle(section.id)}
                sx={{
                  px: 2.5, py: 0.75, minHeight: 36,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemText
                  primary={section.label.toUpperCase()}
                  primaryTypographyProps={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
                    color: 'rgba(255,255,255,0.45)',
                  }}
                />
                {expandedSections[section.id] ? (
                  <ExpandLess sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />
                )}
              </ListItemButton>
            ) : (
              <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.08)' }} />
            )}

            <Collapse in={open ? expandedSections[section.id] : true} timeout="auto" unmountOnExit={false}>
              <List component="div" disablePadding>
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  const button = (
                    <ListItemButton
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      sx={{
                        px: open ? 3 : 2.5, py: 1, minHeight: 44,
                        justifyContent: open ? 'initial' : 'center',
                        borderRadius: open ? '0 24px 24px 0' : '8px',
                        mx: open ? 0 : 0.75, mb: 0.25,
                        backgroundColor: active ? 'rgba(0, 184, 148, 0.15)' : 'transparent',
                        borderLeft: active && open ? `3px solid ${SECONDARY_COLOR}` : '3px solid transparent',
                        '&:hover': {
                          backgroundColor: active ? 'rgba(0, 184, 148, 0.2)' : 'rgba(255,255,255,0.06)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: open ? 40 : 'unset',
                        color: active ? SECONDARY_COLOR : 'rgba(255,255,255,0.6)',
                        justifyContent: 'center',
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: 14, fontWeight: active ? 600 : 400,
                            color: active ? '#ffffff' : 'rgba(255,255,255,0.78)',
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                  return open ? (
                    <React.Fragment key={item.path}>{button}</React.Fragment>
                  ) : (
                    <Tooltip key={item.path} title={item.label} placement="right" arrow>
                      {button}
                    </Tooltip>
                  );
                })}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <Box sx={{ p: open ? 2 : 1, textAlign: 'center' }}>
        {open && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            iMoney Referral System v2.0
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
